import { LANGUAGE_CODE } from '@getyoti/react-face-capture';
import { PropsWithChildren } from 'react';
import Config from '@utils/config';
import { TwoSidesPanel } from '@components/commons';
import {
    AgeDisplayMode,
    AgeEstimationModelField,
    DeceptionType,
    Endpoint,
    ExpectedBrightness,
    ExpectedLocation,
    ExpectedResult,
    LevelOfAssuranceField,
    MetadataField,
    OperatorField,
    SecureMode,
    MultiframeMode,
    ImageValidationLevelField,
    AntispoofingModelField,
} from '@models/prediction';
import styles from './configPanel.module.scss';
import { EndpointConfig, EndpointConfigClassType } from './endpoints/endpointClasses';
import { MediaQuery, useMediaQuery } from '@hooks';

export interface EndpointConfigElement {
    key: Endpoint;
    cfg?: EndpointConfig;
}

export class ConfigProps {
    constructor(
        public selectedEndpoint: Endpoint = Config.defaultConfig.endpoint,
        public selectedSecureMode: SecureMode = Config.defaultConfig.mode,
        public selectedMultiframeMode: MultiframeMode = Config.defaultConfig.multiframeMode,
        public selectedMetadataField?: MetadataField,
        public selectedImageValidationLevelField?: ImageValidationLevelField,
        public selectedLevelOfAssuranceField?: LevelOfAssuranceField,
        public selectedAgeEstimationModelField?: AgeEstimationModelField,
        public selectedAntispoofingModelField?: AntispoofingModelField,
        public selectedOperatorField?: OperatorField,
        public selectedThresholdField?: number,
        public selectedExpectedResult?: ExpectedResult,
        public selectedExpectedLocation?: ExpectedLocation,
        public selectedDeceptionTypes?: DeceptionType[],
        public selectedExpectedBrightness?: ExpectedBrightness,
        public endpoints: EndpointConfigElement[] = [
            { key: Endpoint.Antispoofing },
            { key: Endpoint.Age },
            { key: Endpoint.AgeAntispoofing },
            { key: Endpoint.FaceMatching },
            { key: Endpoint.AgeAntispoofingVerify },
            { key: Endpoint.AgeVerify },
            { key: Endpoint.SelfCheckoutAntiSpoofing },
        ],
        public selectedAgeDisplayMode: AgeDisplayMode = Config.defaultConfig.ageDisplayMode,
        public selectedLanguage: LANGUAGE_CODE = Config.defaultConfig.language,
        public consent: boolean = false,
    ) {}
}

export const fillUndefinedValues = (config: ConfigProps) => {
    if (!config.selectedLanguage) config.selectedLanguage = Config.defaultConfig.language;
};

/**
 * TypeGuard for the ConfigProps type.
 * @param config Object that will be checked.
 */
export function isConfig(config: unknown): config is ConfigProps {
    const typedConfig = config as ConfigProps;

    // Check the properties exist in order to determine if the type is correct.
    if (
        !typedConfig.selectedEndpoint ||
        !typedConfig.endpoints ||
        !typedConfig.endpoints.length ||
        !Object.values(Endpoint).includes(typedConfig.selectedEndpoint)
    ) {
        return false;
    }

    // Check that the selected metadata is valid if it exists.
    if (
        typedConfig.selectedMetadataField &&
        !Object.values(MetadataField)?.includes(typedConfig.selectedMetadataField)
    ) {
        return false;
    }

    // Check that the selected level of assurance is valid if it exists.
    if (
        typedConfig.selectedLevelOfAssuranceField &&
        (!Object.values(LevelOfAssuranceField)?.includes(
            typedConfig.selectedLevelOfAssuranceField,
        ) ||
            !Config.levelAssuranceValues.includes(typedConfig.selectedLevelOfAssuranceField))
    ) {
        return false;
    }

    // Check that the selected age estimation model is valid if it exists.
    if (
        typedConfig.selectedAgeEstimationModelField &&
        !Object.values(AgeEstimationModelField)?.includes(
            typedConfig.selectedAgeEstimationModelField,
        )
    ) {
        return false;
    }

    // Check that the selected operator is valid if it exists.
    if (
        typedConfig.selectedOperatorField &&
        !Object.values(OperatorField)?.includes(typedConfig.selectedOperatorField)
    ) {
        return false;
    }
    // Check that the selected threshold is valid if it exists.
    if (
        typedConfig.selectedThresholdField &&
        typedConfig.selectedThresholdField >= 13 &&
        typedConfig.selectedThresholdField <= 65
    ) {
        return false;
    }
    // Check that the selected expected result is valid if it exists.
    if (
        typedConfig.selectedExpectedResult &&
        !Object.values(ExpectedResult)?.includes(typedConfig.selectedExpectedResult)
    ) {
        return false;
    }

    // Check that the selected expected location is valid if it exists.
    if (
        typedConfig.selectedExpectedLocation &&
        !Object.values(ExpectedLocation)?.includes(typedConfig.selectedExpectedLocation)
    ) {
        return false;
    }

    // Check that the selected expected brightness is valid if it exists.
    if (
        typedConfig.selectedExpectedBrightness &&
        !Object.values(ExpectedBrightness)?.includes(typedConfig.selectedExpectedBrightness)
    ) {
        return false;
    }

    // Check that the selected deception type is valid if it exists.
    if (typedConfig.selectedDeceptionTypes) {
        const deceptionTypes = Object.values(DeceptionType);
        for (let i = 0; i < typedConfig.selectedDeceptionTypes.length; i++) {
            if (!deceptionTypes?.includes(typedConfig.selectedDeceptionTypes[i])) {
                return false;
            }
        }
    }

    // Check that the secure mode exists.
    if (
        !typedConfig.selectedSecureMode ||
        !Object.values(SecureMode).includes(typedConfig.selectedSecureMode)
    ) {
        return false;
    }

    // Check that image validation level is valid if it exists
    if (
        typedConfig.selectedImageValidationLevelField &&
        !Object.values(ImageValidationLevelField)?.includes(
            typedConfig.selectedImageValidationLevelField,
        )
    ) {
        return false;
    }

    // Check that the selected antispoofing model is valid .
    if (
        typedConfig.selectedAntispoofingModelField &&
        !Object.values(AntispoofingModelField)?.includes(typedConfig.selectedAntispoofingModelField)
    ) {
        return false;
    }

    const endpointConfigTypesWrong = typedConfig.endpoints.filter((endpoint) => {
        return endpoint.cfg !== EndpointConfigClassType[endpoint.key];
    });

    return endpointConfigTypesWrong.length <= 0;
}

export interface ConfigSubpanelProps {
    config: ConfigProps;
    onConfigChange: (config: ConfigProps) => void;
    onError: (error: Error) => void;
}

const STORAGE_MESSAGE = 'Configuration is automatically stored on the browser cache.';

const StorageMessage = () => (
    <div className={styles['panel__storage-message']} data-qa={'storage-message'}>
        {STORAGE_MESSAGE}
    </div>
);
interface ConfigPanelProps {
    twoSidesPanelDataQa?: string;
}

const ConfigPanel = ({
    children,
    twoSidesPanelDataQa = 'config-two-sides-panel',
}: PropsWithChildren<ConfigPanelProps>) => {
    const isLaptop = useMediaQuery(MediaQuery.lg);

    return (
        <>
            <TwoSidesPanel data-qa={twoSidesPanelDataQa}>
                {{
                    Left: (
                        <div className={styles['panel__left-container']}>
                            <div className={styles['panel__children']}>{children}</div>
                            {!isLaptop && <StorageMessage />}
                        </div>
                    ),
                    Right: <>{isLaptop && <StorageMessage />}</>,
                    Header: <>{`v${Config.version}`}</>,
                }}
            </TwoSidesPanel>
        </>
    );
};

export default ConfigPanel;
